import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Tooltip } from '@heroui/react';
import { DataGrid, type DataGridColumn } from '@heroui-pro/react';
import {
    buildDataGridSkeletonColumns,
    buildDataGridSkeletonRows,
} from '@/components';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { CategoryRow } from '../../categoriesMeta';

type CategoryTableProps = {
    ariaLabel: string;
    rows: CategoryRow[];
    /**
     * The set is still in flight. The grid keeps its real column headers and
     * skeletons only the rows — an empty list while loading means "not known
     * yet", never "no categories".
     */
    isLoading?: boolean;
    /** `null` for a session that may not change anything. */
    onRename: ((id: string) => void) | null;
    /** `null` for a session that may not change anything. */
    onDelete: ((id: string) => void) | null;
};

/**
 * The tenant's categories, in their own display order.
 *
 * **Both languages on every row, always.** Which one leads is the reader's, and
 * the other sits under it muted — this is the one screen where a tenant edits
 * the pair, so seeing only the half they read would hide the half they are
 * about to leave stale.
 *
 * A row whose other language is empty renders nothing there rather than
 * repeating the first: that is a normal state once the set is the tenant's, and
 * the same string twice reads as a duplicate rather than as a translation.
 *
 * The actions are icon-only and named in visually hidden spans rather than with
 * `aria-label` — HeroUI's Tooltip wrapper drops an `aria-label` from its
 * trigger, leaving the button nameless. Each name carries the category, so a
 * screen reader's element list distinguishes "Rename Spending" from "Rename
 * Payroll".
 */
const SKELETON_ROW_COUNT = 6;

export const CategoryTable: React.FC<CategoryTableProps> = ({
    ariaLabel,
    rows,
    isLoading = false,
    onRename,
    onDelete,
}) => {
    const { t } = useTranslation('pages');

    const columns = React.useMemo<DataGridColumn<CategoryRow>[]>(
        () => [
            {
                id: 'category',
                header: t('operations.categories.columns.category'),
                isRowHeader: true,
                minWidth: 260,
                cell: (row) => (
                    <span className="flex flex-col gap-0.5">
                        <span className="font-medium text-foreground">
                            {row.label}
                        </span>
                        {row.secondaryLabel ? (
                            <span className="text-xs text-muted">
                                {row.secondaryLabel}
                            </span>
                        ) : null}
                    </span>
                ),
            },
            {
                id: 'actions',
                header: (
                    <span className="sr-only">
                        {t('operations.categories.columns.actions')}
                    </span>
                ),
                align: 'end',
                minWidth: 120,
                cell: (row) => (
                    <span className="flex items-center justify-end gap-1">
                        {onRename ? (
                            <Tooltip delay={0}>
                                <Button
                                    isIconOnly
                                    onPress={() => onRename(row.id)}
                                    size="sm"
                                    variant="ghost"
                                    {...getNodeTestIdProp(
                                        `operation-categories-rename-${row.id}`,
                                    )}
                                >
                                    <Icon
                                        aria-hidden
                                        icon="gravity-ui:pencil"
                                    />
                                    <span className="sr-only">
                                        {t(
                                            'operations.categories.actions.renameNamed',
                                            { name: row.label },
                                        )}
                                    </span>
                                </Button>
                                <Tooltip.Content>
                                    <p>
                                        {t(
                                            'operations.categories.actions.rename',
                                        )}
                                    </p>
                                </Tooltip.Content>
                            </Tooltip>
                        ) : null}
                        {onDelete ? (
                            <Tooltip delay={0}>
                                <Button
                                    isIconOnly
                                    onPress={() => onDelete(row.id)}
                                    size="sm"
                                    variant="ghost"
                                    {...getNodeTestIdProp(
                                        `operation-categories-delete-${row.id}`,
                                    )}
                                >
                                    <Icon
                                        aria-hidden
                                        icon="gravity-ui:trash-bin"
                                    />
                                    <span className="sr-only">
                                        {t(
                                            'operations.categories.actions.removeNamed',
                                            { name: row.label },
                                        )}
                                    </span>
                                </Button>
                                <Tooltip.Content>
                                    <p>
                                        {t(
                                            'operations.categories.actions.remove',
                                        )}
                                    </p>
                                </Tooltip.Content>
                            </Tooltip>
                        ) : null}
                    </span>
                ),
            },
        ],
        [onDelete, onRename, t],
    );

    // `contents` wrapper: `DataGrid` forwards no unknown props, so the table's
    // testid has nowhere to land on the grid itself.
    return (
        <div
            className="contents"
            {...getNodeTestIdProp('operation-categories-table')}
        >
            {isLoading ? (
                <DataGrid<{ id: string }>
                    aria-label={ariaLabel}
                    className="ledger-data-grid"
                    columns={buildDataGridSkeletonColumns(columns)}
                    data={buildDataGridSkeletonRows(
                        SKELETON_ROW_COUNT,
                        'operation-categories',
                    )}
                    getRowId={(row) => row.id}
                />
            ) : (
                <DataGrid<CategoryRow>
                    aria-label={ariaLabel}
                    className="ledger-data-grid"
                    columns={columns}
                    data={rows}
                    getRowId={(row) => row.id}
                />
            )}
        </div>
    );
};
